/**
 * Developer Insights Dashboard (Enterprise)
 * Quality scoring, standards adherence, and LLM-powered coaching
 *
 * Route: /insights (enterprise feature gate)
 * API: GET /api/analytics/developer-scores?project_id=X
 * API: GET /api/analytics/coaching?user_id=xxx&project_id=xxx
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Spinner, Button, Select, Alert } from 'flowbite-react';
import {
  HiUserGroup,
  HiShieldCheck,
  HiExclamation,
  HiTrendingUp,
  HiTrendingDown,
  HiMinus,
  HiChartBar,
  HiRefresh,
  HiChevronDown,
  HiChevronUp,
  HiLightBulb,
  HiAcademicCap,
  HiStar,
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { analyticsApi } from '../api/analytics';
import { projectsApi } from '../api/projects';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────

interface ViolationsByCategory {
  [category: string]: number;
}

interface Developer {
  user_email: string;
  display_name: string;
  quality_score: number;
  standards_adherence: number;
  patterns_used: number;
  violations: number;
  violations_by_category: ViolationsByCategory;
  trend: 'improving' | 'stable' | 'declining';
  last_active: string | null;
}

interface TeamSummary {
  total_developers: number;
  avg_quality_score: number;
  standards_adherence: number;
  total_violations: number;
  period: string;
}

interface TrendDataPoint {
  date: string;
  avg_score: number;
}

interface DeveloperScoresResponse {
  success: boolean;
  data?: {
    team_summary: TeamSummary;
    developers: Developer[];
    trend_data: TrendDataPoint[];
  };
  error?: string;
}

interface CoachingRecommendation {
  category: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  suggested_standards: string[];
  evidence: {
    violations: number;
    most_recent: string | null;
  };
}

interface CoachingResponse {
  success: boolean;
  data?: {
    user_email: string;
    recommendations: CoachingRecommendation[];
    strengths: string[];
    generated_at: string;
  };
  error?: string;
}

type SortField = 'display_name' | 'quality_score' | 'standards_adherence' | 'violations' | 'last_active';
type SortDirection = 'asc' | 'desc';

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────

const DeveloperInsights: React.FC = () => {
  const { hasFeature } = useStatus();

  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamSummary, setTeamSummary] = useState<TeamSummary | null>(null);
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [trendData, setTrendData] = useState<TrendDataPoint[]>([]);

  // UI state
  const [period, setPeriod] = useState<'30d' | '90d'>('30d');
  const [sortField, setSortField] = useState<SortField>('quality_score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedDeveloper, setExpandedDeveloper] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Coaching state
  const [coachingData, setCoachingData] = useState<Record<string, CoachingResponse['data']>>({});
  const [coachingLoading, setCoachingLoading] = useState<string | null>(null);

  // Project state
  const [projectId, setProjectId] = useState('');

  const [projectsLoading, setProjectsLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setLoading(true);
    setError(null);
    try {
      const response = await projectsApi.list();
      if (!response.success || !Array.isArray(response.data)) {
        setError(response.error || 'Failed to load projects. Please retry.');
      } else if (response.data.length === 0) {
        setError('No project available. Please create or join a project first.');
      } else {
        setProjectId(response.data[0].project_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects. Please retry.');
    } finally {
      setProjectsLoading(false);
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadScores = useCallback(async () => {
    if (!projectId) return;

    setError(null);
    setTeamSummary(null);
    setDevelopers([]);
    setTrendData([]);

    try {
      const result = await analyticsApi.getDeveloperScores(projectId, period);

      if (result.success && result.data) {
        const responseData = (result.data as any).data || result.data;
        setTeamSummary(responseData.team_summary || null);
        setDevelopers(responseData.developers || []);
        setTrendData(responseData.trend_data || []);
      } else {
        setError(result.error || 'Failed to load developer scores');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load developer insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [projectId, period]);

  useEffect(() => {
    if (projectsLoading || !projectId) return;
    setLoading(true);
    loadScores();
  }, [loadScores, projectsLoading, projectId]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (!projectId) loadProjects();
    else loadScores();
  };

  const loadCoaching = async (userEmail: string) => {
    if (coachingData[userEmail]) return; // Already loaded

    setCoachingLoading(userEmail);

    try {
      const result = await analyticsApi.getCoaching(projectId, userEmail);

      if (result.success && result.data) {
        const responseData = (result.data as any).data || result.data;
        setCoachingData(prev => ({ ...prev, [userEmail]: responseData }));
      }
    } catch (err) {
      console.error('[DeveloperInsights] Failed to load coaching for', userEmail, err);
    } finally {
      setCoachingLoading(null);
    }
  };

  const handleRowClick = (userEmail: string) => {
    if (expandedDeveloper === userEmail) {
      setExpandedDeveloper(null);
    } else {
      setExpandedDeveloper(userEmail);
      loadCoaching(userEmail);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'display_name' ? 'asc' : 'desc');
    }
  };

  // Sort developers
  const sortedDevelopers = [...developers].sort((a, b) => {
    let aVal: string | number = a[sortField] ?? '';
    let bVal: string | number = b[sortField] ?? '';

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Feature gate
  if (!hasFeature('analytics')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <HiChartBar className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Enterprise Feature</h2>
        <p className="text-gray-500 text-center max-w-md">
          Developer Insights is available on Professional and Enterprise plans.
          Upgrade to access quality scoring, standards adherence tracking, and AI-powered coaching.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Developer Insights</h1>
          <p className="mt-1 text-sm text-gray-500">
            Quality scores, standards adherence, and coaching recommendations
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value as '30d' | '90d')}
          >
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </Select>
          <Button
            color="gray"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <HiRefresh className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="failure">
          {error}
        </Alert>
      )}

      {/* Team Overview Cards */}
      {teamSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <HiUserGroup className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Developers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {teamSummary.total_developers}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <HiTrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Avg Quality Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {teamSummary.avg_quality_score}
                  <span className="text-sm font-normal text-gray-400">/100</span>
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <HiShieldCheck className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Standards Adherence</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.round(teamSummary.standards_adherence * 100)}%
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gray-100">
                <HiExclamation className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Violations</p>
                <p className="text-sm text-gray-400 mt-0.5">Not yet tracked</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Developer Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Developer Scores</h3>
          <div className="text-sm text-gray-500">
            Click a row to view coaching recommendations
          </div>
        </div>

        {sortedDevelopers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-700 bg-gray-50">
                <tr>
                  <SortableHeader
                    label="Developer"
                    field="display_name"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Quality Score"
                    field="quality_score"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="center"
                  />
                  <SortableHeader
                    label="Standards Adherence"
                    field="standards_adherence"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="center"
                  />
                  <SortableHeader
                    label="Violations"
                    field="violations"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="center"
                  />
                  <SortableHeader
                    label="Last Active"
                    field="last_active"
                    currentField={sortField}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                  <th className="px-4 py-3 text-center">Trend</th>
                </tr>
              </thead>
              <tbody>
                {sortedDevelopers.map((dev) => (
                  <React.Fragment key={dev.user_email}>
                    <tr
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(dev.user_email)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          {expandedDeveloper === dev.user_email ? (
                            <HiChevronUp className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          ) : (
                            <HiChevronDown className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{dev.display_name}</p>
                            <p className="text-xs text-gray-500">{dev.user_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <QualityScoreBadge score={dev.quality_score} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium">
                          {Math.round(dev.standards_adherence * 100)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {dev.violations > 0 ? (
                          <Badge color="failure">{dev.violations}</Badge>
                        ) : (
                          <span className="text-sm text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {dev.last_active
                          ? new Date(dev.last_active).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <TrendIndicator trend={dev.trend} />
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {expandedDeveloper === dev.user_email && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-gray-50">
                          <DeveloperDetailPanel
                            developer={dev}
                            coaching={coachingData[dev.user_email] || null}
                            isLoading={coachingLoading === dev.user_email}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <HiUserGroup className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No developer data</h3>
            <p className="mt-1 text-sm text-gray-500">
              Developer scores will appear once team members begin using MindMeld standards.
            </p>
          </div>
        )}
      </Card>

      {/* Quality Trend Section */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Quality Trend</h3>
          <Badge color="info">{period === '90d' ? '90-day' : '30-day'} trend</Badge>
        </div>

        {trendData.length > 0 ? (
          <div>
            <div className="flex items-end space-x-1 h-48">
              {trendData.map((point) => {
                const height = Math.max(4, (point.avg_score / 100) * 100);
                const barColor =
                  point.avg_score >= 80
                    ? 'bg-green-500'
                    : point.avg_score >= 60
                      ? 'bg-yellow-500'
                      : 'bg-red-500';

                return (
                  <div
                    key={point.date}
                    className="flex-1 flex flex-col items-center justify-end group relative"
                  >
                    <div className="hidden group-hover:block absolute -top-8 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                      {point.date}: {point.avg_score}
                    </div>
                    <div
                      className={`w-full rounded-t ${barColor} transition-all`}
                      style={{ height: `${height}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{trendData[0]?.date}</span>
              <span>{trendData[trendData.length - 1]?.date}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <HiChartBar className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p>No trend data available yet</p>
            <p className="text-sm">Trend data populates as developers use standards over time</p>
          </div>
        )}
      </Card>
    </div>
  );
};

// ──────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────

const SortableHeader: React.FC<{
  label: string;
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'center' | 'right';
}> = ({ label, field, currentField, direction, onSort, align = 'left' }) => {
  const isActive = currentField === field;
  const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

  return (
    <th
      className={`px-4 py-3 ${textAlign} cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        {isActive && (
          <span className="ml-1">
            {direction === 'asc' ? (
              <HiChevronUp className="h-4 w-4" />
            ) : (
              <HiChevronDown className="h-4 w-4" />
            )}
          </span>
        )}
      </div>
    </th>
  );
};

const QualityScoreBadge: React.FC<{ score: number }> = ({ score }) => {
  let color: 'success' | 'warning' | 'failure' = 'success';
  if (score < 50) color = 'failure';
  else if (score < 70) color = 'warning';

  return (
    <Badge color={color} size="lg">
      {score}
    </Badge>
  );
};

const TrendIndicator: React.FC<{ trend: Developer['trend'] }> = ({ trend }) => {
  switch (trend) {
    case 'improving':
      return (
        <span className="inline-flex items-center text-green-600" title="Improving">
          <HiTrendingUp className="h-5 w-5" />
        </span>
      );
    case 'declining':
      return (
        <span className="inline-flex items-center text-red-600" title="Declining">
          <HiTrendingDown className="h-5 w-5" />
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center text-gray-400" title="Stable">
          <HiMinus className="h-5 w-5" />
        </span>
      );
  }
};

const DeveloperDetailPanel: React.FC<{
  developer: Developer;
  coaching: CoachingResponse['data'] | null;
  isLoading: boolean;
}> = ({ developer, coaching, isLoading }) => {
  return (
    <div className="space-y-4">
      {/* Violation Breakdown */}
      {developer.violations > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Violations by Category</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(developer.violations_by_category).map(([category, count]) => (
              <Badge key={category} color="failure">
                {category}: {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Patterns Used */}
      <div className="flex items-center space-x-6 text-sm">
        <div>
          <span className="text-gray-500">Patterns used: </span>
          <span className="font-medium">{developer.patterns_used}</span>
        </div>
        <div>
          <span className="text-gray-500">Quality score: </span>
          <span className="font-medium">{developer.quality_score}/100</span>
        </div>
        <div>
          <span className="text-gray-500">Standards adherence: </span>
          <span className="font-medium">{Math.round(developer.standards_adherence * 100)}%</span>
        </div>
      </div>

      {/* Coaching Section */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <HiAcademicCap className="h-5 w-5 mr-2 text-purple-600" />
          Coaching Recommendations
        </h4>

        {isLoading && (
          <div className="flex items-center space-x-2 text-gray-500">
            <Spinner size="sm" />
            <span className="text-sm">Generating coaching recommendations...</span>
          </div>
        )}

        {coaching && !isLoading && (
          <div className="space-y-4">
            {/* Strengths */}
            {coaching.strengths.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center">
                  <HiStar className="h-4 w-4 mr-1 text-yellow-500" />
                  Strengths
                </h5>
                <div className="flex flex-wrap gap-2">
                  {coaching.strengths.map((strength, idx) => (
                    <Badge key={idx} color="success">
                      {strength}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {coaching.recommendations.length > 0 ? (
              <div className="space-y-3">
                {coaching.recommendations.map((rec, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border-l-4 ${
                      rec.priority === 'high'
                        ? 'bg-red-50 border-red-500'
                        : rec.priority === 'medium'
                          ? 'bg-yellow-50 border-yellow-500'
                          : 'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{rec.title}</span>
                      <div className="flex items-center space-x-2">
                        <Badge color="gray">{rec.category}</Badge>
                        <Badge
                          color={
                            rec.priority === 'high'
                              ? 'failure'
                              : rec.priority === 'medium'
                                ? 'warning'
                                : 'info'
                          }
                        >
                          {rec.priority}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{rec.description}</p>
                    {rec.suggested_standards.length > 0 && (
                      <div className="flex items-center space-x-1">
                        <HiLightBulb className="h-4 w-4 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          Suggested standards: {rec.suggested_standards.join(', ')}
                        </span>
                      </div>
                    )}
                    {rec.evidence.violations > 0 && (
                      <div className="text-xs text-gray-400 mt-1">
                        {rec.evidence.violations} violation(s)
                        {rec.evidence.most_recent && ` | Most recent: ${rec.evidence.most_recent}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <HiAcademicCap className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                <p className="text-sm">No coaching recommendations at this time</p>
              </div>
            )}
          </div>
        )}

        {!coaching && !isLoading && (
          <div className="text-center py-4 text-gray-400">
            <p className="text-sm">Click to load coaching recommendations</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeveloperInsights;
